import React, { useState } from 'react';
import { Dialog, ElevatedButton, TextButton } from '@producer/ui-kit';

import { SingleSelectDropdown } from '../../../components/select-dropdown';
import { getCrewContactOptions } from '../lib/crew-contact-options';
import { updateCrewMember } from '../stores/crew.slice';
import { catchError } from '../../../core/catch-error';

import type { CrewMember } from '../../../app/entities/crew-member';
import type { Project } from '../../../app/entities/project';

import styles from './assign-crew-member.modal.module.scss';

type AssignCrewMemberModalProps = {
  project: Project;
  member: CrewMember;
  onCancel: () => void;
  onClose: () => void;
};

/**
 * The quick-assign path: a single contact picker, reachable from the row's "Assign
 * contact"/"Change contact" menu item and from the "Unassigned" chip itself (both in
 * `crew-list.tsx`). `crew-member.modal.tsx` covers the same field among many others; this one
 * exists so picking (or swapping) a contact never requires opening the full edit form.
 */
export const AssignCrewMemberModal: React.FC<AssignCrewMemberModalProps> = ({
  project,
  member,
  onCancel,
  onClose,
}) => {
  const [contactId, setContactId] = useState<string | undefined>(member.contactId ?? undefined);
  const [isLoading, setIsLoading] = useState(false);

  const contactOptions = getCrewContactOptions(project);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) return;

    setIsLoading(true);

    try {
      await updateCrewMember(member, { contactId: contactId || null });
      onClose();
    } catch (error) {
      setIsLoading(false);
      catchError(error, 'Could not assign this crew member. Try again.');
    }
  };

  return (
    <Dialog
      title={member.isAssigned ? 'Change contact' : 'Assign contact'}
      onClose={onCancel}
      actions={[
        <TextButton key="cancel" text="Cancel" onClick={onCancel} />,
        <ElevatedButton
          key="submit"
          form="assign-crew-member-form"
          type="submit"
          isLoading={isLoading}
          text="Save"
        />,
      ]}
    >
      <form id="assign-crew-member-form" className={styles['form']} onSubmit={onSubmit}>
        <SingleSelectDropdown
          label="Contact"
          title="Contact"
          value={contactId}
          options={contactOptions}
          onValueChange={setContactId}
          placeholder="Choose who fills this position"
        />
      </form>
    </Dialog>
  );
};
